import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

interface RequestMemberSignatureButtonProps {
  documentId: string;
  unaId: string;
  documentName: string;
  onSignaturesRequested?: () => void;
}

export function RequestMemberSignatureButton({
  documentId,
  unaId,
  documentName,
  onSignaturesRequested,
}: RequestMemberSignatureButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const queryClient = useQueryClient();

  // Load active members (excludes Secretary Treasurer, includes members without accounts)
  const { data: members, isLoading, error: membersError } = useQuery({
    queryKey: ["una-members-with-accounts", unaId],
    queryFn: async () => {
      console.log("🔍 Fetching members for UNA:", unaId);
      const { data, error } = await supabase
        .from("una_members")
        .select(`
          id,
          user_id,
          full_name,
          member_role,
          email,
          status
        `)
        .eq("una_id", unaId)
        .eq("status", "active")
        .neq("member_role", "treasury_secretary"); // Exclude Secretary Treasurer by role

      if (error) {
        console.error("❌ Error fetching members:", error);
        throw error;
      }
      
      console.log("✅ Found members:", data?.length || 0, data);
      return data || [];
    },
    enabled: true,  // Always fetch to determine if button should be enabled
  });

  // Check which members have already signed
  const { data: existingSignatures } = useQuery({
    queryKey: ["document-signatures", documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_signatures")
        .select("signer_user_id, member_id")
        .eq("document_id", documentId);

      if (error) throw error;
      return data || [];
    },
    enabled: true, // Always fetch to show button status
  });

  // Check for existing signature requests
  const { data: signatureRequests } = useQuery({
    queryKey: ["signature-requests", documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("signature_requests")
        .select("id, status, document_signers(una_member_id, status)")
        .eq("document_id", documentId);

      if (error) throw error;
      
      // Flatten to get all signers with their status
      const signers: { memberId: string; status: string }[] = [];
      data?.forEach(req => {
        if (req.document_signers && Array.isArray(req.document_signers)) {
          req.document_signers.forEach((signer: { una_member_id: string | null; status: string }) => {
            if (signer.una_member_id) {
              signers.push({ memberId: signer.una_member_id, status: signer.status });
            }
          });
        }
      });
      return signers;
    },
    enabled: true,
  });

  // Calculate signature states
  const membersWithRequests = new Set(signatureRequests?.map(s => s.memberId) || []);
  const pendingRequests = signatureRequests?.filter(s => s.status === 'pending') || [];
  const completedRequests = signatureRequests?.filter(s => s.status === 'completed' || s.status === 'signed') || [];
  
  // Members who have signed (via document_signatures)
  const signedMemberIds = new Set(
    existingSignatures?.map(sig => sig.member_id).filter(Boolean) || []
  );
  
  // Check if all members have completed signatures
  const allSignaturesComplete = members && members.length > 0 && 
    members.every(m => signedMemberIds.has(m.id) || completedRequests.some(r => r.memberId === m.id));

  // Members available to request (not already requested)
  const membersAvailableToRequest = members?.filter(m => !membersWithRequests.has(m.id) && !signedMemberIds.has(m.id)) || [];

  // Send signature requests
  const requestMutation = useMutation({
    mutationFn: async () => {
      console.log("📤 Sending signature requests to members:", selectedMembers);
      
      const requests = selectedMembers.map(async (memberId) => {
        const member = members?.find((m) => m.id === memberId);
        if (!member) {
          console.error("❌ Member not found in local list:", memberId);
          throw new Error(`Member not found: ${memberId}`);
        }

        console.log("📧 Sending request to:", member.full_name, member.email);

        const { data, error } = await supabase.functions.invoke(
          "send-member-signature-request",
          {
            body: {
              documentId,
              memberId: member.id, // una_members.id
              roleSigningAs: member.member_role || "Member",
              capacity: getCapacityFromRole(member.member_role),
              unaId,
            },
          }
        );

        if (error) {
          console.error("❌ Edge function error for", member.full_name, ":", error);
          throw error;
        }
        
        console.log("✅ Request sent successfully to", member.full_name);
        return data;
      });

      await Promise.all(requests);
    },
    onSuccess: () => {
      toast.success(`Signature requests sent to ${selectedMembers.length} member(s)`);
      setIsOpen(false);
      setSelectedMembers([]);
      queryClient.invalidateQueries({ queryKey: ["document-signatures", documentId] });
      queryClient.invalidateQueries({ queryKey: ["signature-requests", documentId] });
      onSignaturesRequested?.();
    },
    onError: (error: Error) => {
      console.error("❌ Mutation error:", error);
      toast.error(`Failed to send signature requests: ${error.message}`);
    },
  });

  const handleToggleMember = (memberId: string, checked: boolean) => {
    if (checked) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers.filter((id) => id !== memberId));
    }
  };

  // Helper to convert member_role to display capacity
  function getCapacityFromRole(role: string | null): string {
    if (!role) return "Member";
    
    const roleMap: Record<string, string> = {
      board_member_active: "Board Member",
      board_member_advisory: "Advisory Board Member",
      general_member: "General Member",
      managing_member: "Managing Member",
      treasury_secretary: "Secretary Treasurer",
    };

    return roleMap[role] || role.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  }

  const hasMembers = members && members.length > 0;

  // Render button based on signature status
  const renderButton = () => {
    if (isLoading) {
      return (
        <Button disabled className="opacity-50">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading Members...
        </Button>
      );
    }

    if (allSignaturesComplete && hasMembers) {
      return (
        <Button disabled className="bg-green-600 hover:bg-green-600 text-white">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          All Signatures Collected
        </Button>
      );
    }

    if (pendingRequests.length > 0) {
      return (
        <Button onClick={() => setIsOpen(true)} variant="secondary">
          <Clock className="w-4 h-4 mr-2" />
          Awaiting Signatures ({pendingRequests.length} pending)
        </Button>
      );
    }

    return (
      <Button onClick={() => setIsOpen(true)} disabled={!hasMembers}>
        <Mail className="w-4 h-4 mr-2" />
        Request Member Signatures
      </Button>
    );
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={!hasMembers && !isLoading ? 0 : undefined}>
              {renderButton()}
            </span>
          </TooltipTrigger>
          {!hasMembers && !isLoading && !allSignaturesComplete && (
            <TooltipContent>
              <p>No members added. Add a member to request member signatures!</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Request Member Signatures</DialogTitle>
            <DialogDescription>
              Select members who should sign <strong>{documentName}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {membersError ? (
              <div className="text-center py-8 text-destructive">
                <p className="font-semibold mb-2">Error loading members</p>
                <p className="text-sm">{membersError.message}</p>
              </div>
            ) : membersAvailableToRequest.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="font-semibold mb-2">
                  {hasMembers 
                    ? "All members have been requested or have already signed" 
                    : "No board members available for signature requests"}
                </p>
                {!hasMembers && (
                  <>
                    <p className="text-sm mt-4">To request signatures on governance documents, you need at least one active board member besides the primary officer.</p>
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left max-w-md mx-auto">
                      <p className="text-sm font-semibold text-amber-900 mb-2">Next Steps:</p>
                      <ol className="text-sm text-amber-800 space-y-1">
                        <li>1. Go to the Members page</li>
                        <li>2. Use "Add a UNA Member" workflow to add board members</li>
                        <li>3. Once members are active, return here to request signatures</li>
                      </ol>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Show members available to request */}
                {membersAvailableToRequest.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-4 p-4 border rounded-lg bg-card hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedMembers.includes(member.id)}
                      onCheckedChange={(checked) =>
                        handleToggleMember(member.id, checked as boolean)
                      }
                    />

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.full_name}</p>
                        {!member.user_id && (
                          <Badge variant="outline" className="text-xs">
                            No account - will receive email
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {getCapacityFromRole(member.member_role)}
                      </p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                ))}

                {/* Show already requested/signed members (read-only) */}
                {members?.filter(m => membersWithRequests.has(m.id) || signedMemberIds.has(m.id)).map((member) => {
                  const hasSigned = signedMemberIds.has(member.id);
                  const isPending = pendingRequests.some(r => r.memberId === member.id);
                  
                  return (
                    <div
                      key={member.id}
                      className="flex items-center gap-4 p-4 border rounded-lg bg-muted opacity-60"
                    >
                      <Checkbox checked disabled />

                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{member.full_name}</p>
                          {hasSigned ? (
                            <Badge className="bg-green-600 text-white">Signed</Badge>
                          ) : isPending ? (
                            <Badge variant="secondary">Request Sent</Badge>
                          ) : null}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {getCapacityFromRole(member.member_role)}
                        </p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => requestMutation.mutate()}
              disabled={selectedMembers.length === 0 || requestMutation.isPending}
            >
              {requestMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                `Send Requests (${selectedMembers.length})`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}